/**
 * DashboardView.tsx - DashboardView - Metrics and analytics dashboard
 * 
 * CHANGE HISTORY:
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import React, { useState, useMemo, useCallback } from 'react';
import { useDatasetView } from '../base/useDatasetView';
import { useViewMetadata } from '../base/useViewMetadata';
import { useBacktrackr } from '@opcentral/backtrackr';
import DashboardWidget from './DashboardWidget';
import './DashboardView.css';

const DashboardView = ({
  viewId = 'dashboard-default',
  datasetContext,
  dataContext,
  backtrackrContext: propBacktrackrContext,
  config = {}
}) => {
  const [selectedWidget, setSelectedWidget] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  
  const {
    items,
    fieldMappings,
    getField,
    updateItem,
    addItem,
    deleteItem
  } = useDatasetView('dashboard', datasetContext, dataContext);
  
  const hookBacktrackrContext = useBacktrackr();
  const backtrackrContext = propBacktrackrContext || hookBacktrackrContext;
  
  const {
    getMetadata,
    setMetadata
  } = useViewMetadata(viewId, items, updateItem);
  
  // Extract config
  const dashboardConfig = config.dashboard || {};
  const widgets = dashboardConfig.widgets || [
    { id: 'total', type: 'kpi', title: 'Total Items', field: 'count' },
    { id: 'status', type: 'pie', title: 'By Status', field: 'status' },
    { id: 'timeline', type: 'line', title: 'Over Time', field: 'createdAt' }
  ];
  
  // Calculate metrics
  const metrics = useMemo(() => {
    const totalItems = items.length;
    
    // Count by status
    const statusCounts = {};
    items.forEach(item => {
      const status = getField(item, 'status') || 'none';
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });
    
    // Count by priority
    const priorityCounts = {};
    items.forEach(item => {
      const priority = getField(item, 'priority') || 'none';
      priorityCounts[priority] = (priorityCounts[priority] || 0) + 1;
    });
    
    // Items created over time (last 7 days)
    const now = new Date();
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date(now);
      date.setDate(date.getDate() - (6 - i));
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });
    
    const itemsByDay = {};
    last7Days.forEach(day => itemsByDay[day] = 0);
    
    items.forEach(item => {
      const createdAt = new Date(getField(item, 'createdAt'));
      const dayLabel = createdAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      if (itemsByDay[dayLabel] !== undefined) {
        itemsByDay[dayLabel]++;
      }
    });
    
    // Completion rate
    const completedCount = items.filter(item => 
      getField(item, 'status') === 'done' || getField(item, 'status') === 'completed'
    ).length;
    const completionRate = totalItems > 0 ? Math.round((completedCount / totalItems) * 100) : 0;
    
    // Average progress
    const totalProgress = items.reduce((sum, item) => {
      return sum + (getField(item, 'progress') || 0);
    }, 0);
    const avgProgress = totalItems > 0 ? Math.round(totalProgress / totalItems) : 0;
    
    return {
      totalItems,
      statusCounts,
      priorityCounts,
      itemsByDay,
      completionRate,
      avgProgress,
      completedCount
    };
  }, [items, getField]);
  
  const renderWidget = useCallback((widget) => {
    return (
      <DashboardWidget
        key={widget.id}
        widget={widget}
        metrics={metrics}
        isSelected={selectedWidget === widget.id}
        isEditMode={isEditMode}
        onSelect={() => setSelectedWidget(widget.id)}
      />
    );
  }, [metrics, selectedWidget, isEditMode]);
  
  return (
    <div className="dashboard-view">
      <div className="dashboard-toolbar">
        <h2>Dashboard</h2>
        
        <div className="dashboard-toolbar-actions">
          <button
            onClick={() => setIsEditMode(!isEditMode)}
            className={`btn-edit-mode ${isEditMode ? 'active' : ''}`}
          >
            {isEditMode ? 'Done Editing' : 'Edit Layout'}
          </button>
          
          <button className="btn-refresh" title="Refresh">
            ↻
          </button>
          
          <button className="btn-export" title="Export">
            ⤓
          </button>
        </div>
      </div>
      
      <div className="dashboard-grid">
        {widgets.map(renderWidget)}
      </div>
      
      {items.length === 0 && (
        <div className="dashboard-empty-state">
          <h3>No data yet</h3>
          <p>Add some items to see your dashboard come to life</p>
        </div>
      )}
    </div>
  );
};

export default DashboardView;
